import {
  ChevronRightIcon,
  HomeIcon,
  XCircleIcon,
} from "@heroicons/react/20/solid";
import * as ScrollArea from "@radix-ui/react-scroll-area";
import clsx from "clsx";
import invariant from "invariant";
import { debounce } from "lodash";
import mixpanel from "mixpanel-browser";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { EnumFeaturePreset } from "../../__generated__/graphql";
import { useModalContext } from "../../contexts/modals";
import { useToastsContext } from "../../contexts/toasts";
import { HandleApiSignOut } from "../../graphql/auth/auth.mutations";
import {
  GetOneDrive,
  ProcessOneDriveAttachment,
} from "../../graphql/auth/auth.queries";
import useStores from "../../hooks/useStores";
import { PricingPlanTier } from "../../models/User";
import { UploadedFile } from "../../types";
import { FeaturePreset } from "../../utils/ProcessAttachments";
import ButtonLarge from "../ButtonLarge";
import FileProcessingQuestions from "../Chat/FileProcessingQuestions";
import type { FileProcessingAnswers } from "../Chat/fileProcessing";
import { fileProcessingConfigs } from "../Chat/fileProcessingConfig";
import { Spinner } from "../LoadingIndicators/Spinner";
import Tooltip from "../Tooltip";
import OneDriveBlock from "./OneDriveBlock";
const WindowsFolder = "/imgs/blocks/windows-folder.png";

const MAX_PDF_SIZE_FOR_PROCESSING = 15 * 1024 * 1024; // 15MB in bytes

function pricingPlanTierToMaxUploadSize(currentPlan: PricingPlanTier) {
  switch (currentPlan) {
    case PricingPlanTier.Basic:
      return (
        Number(import.meta.env.VITE_APP_BASIC_PLAN_MAX_UPLOAD_SIZE) ?? 31457280 // 30MB
      );
    case PricingPlanTier.Pro:
      return (
        Number(import.meta.env.VITE_APP_PRO_PLAN_MAX_UPLOAD_SIZE) ?? 31457280 // 30 MB
      );
    default:
      return (
        Number(import.meta.env.VITE_APP_ENTERPRISE_PLAN_MAX_UPLOAD_SIZE) ??
        31457280 // 30MB
      );
  }
}

export function bytesToReadable(bytes: number | undefined) {
  if (!bytes) {
    return "0 Bytes";
  }

  const out = ("0".repeat((bytes.toString().length * 2) % 3) + bytes).match(
    /.{3}/g
  );

  if (!out || bytes < 1000) {
    return bytes + " Bytes";
  }

  const f = out[1].substring(0, 2);

  return `${Number(out[0])}${f === "00" ? "" : `.${f}`} ${
    "  kMGTPEZY"[out.length]
  }B`;
}

type Props = {
  setAttachment: (attachment: UploadedFile) => void;
  feature: FeaturePreset;
};

export default function OneDriveBlocks(props: Props) {
  const { feature } = props;
  const { auth, assets } = useStores();
  const { addToast } = useToastsContext();
  const { addModal, closeModal, closeModalAll } = useModalContext();
  const [nestedFolderIds, setNestedFolderIds] = useState<any>([]);
  const [documents, setDocuments] = useState([]);
  const [isLoadingOneDriveApi, setIsLoadingOneDriveApi] = useState(false);
  const [signedInUser, setSignedInUser] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [fileUploadError, setFileUploadError] = useState<string | null>(null);
  const [isFetchingOneDriveFiles, setIsFetchingOneDriveFiles] =
    useState<boolean>(false);

  const [processingMessage, setProcessingMessage] = useState<string>("");
  const { user } = auth;

  const currentUserPlan = user ? user.currentPlan : PricingPlanTier.Basic;

  /**
   * Load files from OneDrive
   * @param searchTerm - Search term
   * @param folderId - Folder ID
   * @returns void
   */
  const loadFiles = useCallback(
    (searchTerm?: string, folderId?: string) => {
      console.log("Loading files", searchTerm, folderId);

      setIsFetchingOneDriveFiles(true);

      auth.apolloClient
        .query({
          query: GetOneDrive,
          variables: {
            oneDriveInput: {
              userId: auth.user?.id,
              searchTerm: searchTerm,
              folderId: folderId,
            },
          },
        })
        .then((res: any) => {
          if (res && res.data && res.data.getOneDrive) {
            try {
              console.log(JSON.parse(res.data.getOneDrive));
              const oneDriveObject = JSON.parse(res.data.getOneDrive);
              setDocuments(oneDriveObject.value || []);
              setIsLoadingOneDriveApi(false);
              return;
            } catch (err) {
              console.log(err);
              setIsLoadingOneDriveApi(false);
            }
          }
        })
        .catch((err: any) => {
          console.log(err);
        })
        .finally(() => {
          setIsFetchingOneDriveFiles(false);
        });
    },
    [auth]
  );

  // Use Effect to set the Sign In State
  useEffect(() => {
    const integrations = auth && auth.user && auth.user.integrations;

    console.log("Integrations", integrations);

    if (integrations && integrations["one-drive"]) {
      console.log("List files");

      setSignedInUser({
        name: integrations["one-drive"].name,
        email: integrations["one-drive"].email,
      });
    }
  }, [auth.user?.integrations]);

  // Debounce the search function
  const debouncedSearch = useCallback(
    debounce((searchTerm: string, folderId?: string) => {
      loadFiles(searchTerm, folderId);
    }, 300), // Adjust the delay as needed
    [] // Add any dependencies here if necessary
  );

  // Effect to handle search input changes
  useEffect(() => {
    if (!signedInUser) {
      return;
    }

    const folderId =
      nestedFolderIds.length > 0
        ? nestedFolderIds[nestedFolderIds.length - 1].id
        : null;

    if (searchTerm === "") {
      loadFiles("", folderId);
    } else {
      debouncedSearch(searchTerm, folderId);
    }
  }, [signedInUser, searchTerm, nestedFolderIds, debouncedSearch]);

  console.log("Documents", documents);

  /**
   * Process OneDrive attachment with optional processing questions
   */
  const processOneDriveAttachment = useCallback(
    async (fileId: any, title: any, fileType: any, size: number) => {
      setIsLoadingOneDriveApi(true);
      setProcessingMessage(`Processing ${title}...`);

      const config = fileProcessingConfigs.find((cfg) =>
        cfg.mimeType.includes(fileType)
      );

      const isPDF = fileType === "application/pdf";
      const exceedsSizeLimit = isPDF && size > MAX_PDF_SIZE_FOR_PROCESSING;

      const processFile = async (
        answers: FileProcessingAnswers = {},
        modalId?: string
      ) => {
        console.log("Process file called with modalId", modalId);
        if (modalId) {
          closeModal(modalId);
        }

        setProcessingMessage(`Uploading ${title}...`);

        auth.apolloClient
          .mutate({
            mutation: ProcessOneDriveAttachment,
            variables: {
              oneDriveAttachmentInput: {
                fileId,
                userId: auth.user?.id,
                type: fileType,
                title,
                feature: props.feature as unknown as EnumFeaturePreset,
                processingOptions: isPDF
                  ? {
                      ...answers,
                      hasHandwriting: exceedsSizeLimit
                        ? false
                        : answers.hasHandwriting,
                      processImages: exceedsSizeLimit
                        ? false
                        : answers.processImages,
                    }
                  : answers,
              },
            },
          })
          .then(async (res) => {
            if (res && res.data && res.data.processOneDriveAttachment) {
              const a = res.data.processOneDriveAttachment;

              invariant(a.user, "Asset must have a user.");
              invariant(a.assetType, "Asset must have an assetType.");

              const sanitizedAsset = {
                id: a.id,
                key: a.key,
                acl: a.acl,
                size: a.size,
                contentType: a.contentType,
                assetType: a.assetType,
                content: a.content,
                userId: a.user.id,
                courseId: a.course ? a.course.id : null,
                resourceId: a.resource ? a.resource.id : null,
                createdAt: a.createdAt,
                updatedAt: a.updatedAt,
                deletedAt: a.deletedAt,
                vectorEmbeddings: a.vectorEmbeddings,
              };

              const mixpanelTrackingId =
                feature === FeaturePreset.InstantResources
                  ? "new-file-upload-instant-resources"
                  : "new-file-upload-curriculum-creator";

              // Add to mixpanel
              mixpanel.track(mixpanelTrackingId, {
                assetId: a.id,
                uploadType: "ONE_DRIVE",
                processingOptions: answers,
              });

              const asset = await assets.add(sanitizedAsset);

              console.log("Asset added to Store", asset);

              const attachment = {
                asset,
                resourceUrl: "Not_needed",
                title: "Non_Needed",
              };

              props.setAttachment(attachment);
              closeModalAll();
            }
          })
          .catch((err: any) => {
            console.log("Error message", err.message);
            setFileUploadError(
              "Failed to import file - " + title + ". " + err.message
            );
            setIsLoadingOneDriveApi(false);
            setProcessingMessage("");
          });
      };

      // If we have a config for this file type and it's not a large PDF, show processing questions
      if (config && !exceedsSizeLimit) {
        const dummyFile = new File([], title, { type: fileType });

        const modalId = addModal({
          content: (
            <FileProcessingQuestions
              file={dummyFile}
              config={config}
              onComplete={async (answers: FileProcessingAnswers) => {
                await processFile(answers, modalId);
              }}
            />
          ),
          size: "md",
          title: `Processing Options for ${title}`,
        });
      } else {
        // No processing questions needed or PDF exceeds size limit, proceed directly
        console.log(
          exceedsSizeLimit
            ? "PDF exceeds 15MB size limit for visual processing"
            : "No config found for file type",
          fileType
        );
        await processFile(
          isPDF ? { hasHandwriting: false, processImages: false } : {}
        );
      }
    },
    [auth, feature, addModal, closeModalAll]
  );

  /**
   * Handle sign in with OneDrive
   * @returns void
   */
  const handleSignIn = async () => {
    if (auth && auth.user && auth.user.id) {
      const clientId = "55864141-cda0-498a-897a-57677e16d372";
      const scope = "files.readwrite offline_access User.Read";
      const redirectUri = `${
        import.meta.env.MODE === "development"
          ? "http://localhost:8081"
          : import.meta.env.VITE_APP_URL
      }/settings/profile/one-drive`;
      const url =
        "https://login.microsoftonline.com/common/oauth2/v2.0/authorize?client_id=" +
        clientId +
        "&scope=" +
        scope +
        "&response_type=code&redirect_uri=" +
        redirectUri;
      window.location.href = url;
    }
  };

  /**
   * Handle sign out from OneDrive
   * @returns void
   */
  const handleSignOut = async () => {
    if (auth && auth.user && auth.user.id) {
      const userId: string = auth.user.id;
      auth.apolloClient
        .mutate({
          mutation: HandleApiSignOut,
          variables: {
            tokenInput: {
              userId,
              type: "one-drive",
            },
          },
        })
        .then((res: any) => {
          console.log(res);
          if (res && res.data && res.data.handleApiSignOut) {
            addToast("OneDrive Disabled.", {
              type: "success",
            });
            auth.fetch().then(() => {
              loadFiles();
            });
          } else {
            addToast("Something went wrong. Please try again later.", {
              type: "error",
            });
          }
        });
    }
  };

  if (isLoadingOneDriveApi) {
    return (
      <div className="flex w-full items-center justify-center py-12 flex-col gap-4">
        <Spinner size={32} color="black" />
        {processingMessage && (
          <p className="text-gray-600 text-sm">{processingMessage}</p>
        )}
      </div>
    );
  }

  if (fileUploadError) {
    return (
      <div className="flex flex-col">
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <XCircleIcon
                className="h-8 w-8 text-red-400"
                aria-hidden="true"
              />
            </div>
            <div className="ml-3">
              <h3 className="text-lg font-medium text-red-800">
                {fileUploadError}
              </h3>
            </div>
          </div>
        </div>
        <div className="mt-8">
          <ButtonLarge
            type="button"
            theme="primary"
            buttonText="Back to OneDrive"
            fullWidth={true}
            onClick={() => {
              setFileUploadError(null);
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full overflow-x-hidden">
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {/* TO-DO */}
        <div
          className={clsx(
            "m-5 flex",
            signedInUser ? "justify-between" : "justify-center"
          )}
        >
          {signedInUser ? (
            <div className="">
              {signedInUser.name ? (
                <div className="font-bold">{signedInUser.name}</div>
              ) : null}{" "}
              {signedInUser.email ? (
                <div className="text-sm">{signedInUser.email}</div>
              ) : null}
            </div>
          ) : null}
          {signedInUser ? (
            <button
              className="align-center h-8 gap-x-3 rounded-lg px-3 py-1 text-center text-sm font-medium text-gray-500 shadow-sm ring-1 ring-inset ring-gray-300"
              style={{
                minWidth: "90px",
              }}
              onClick={() => {
                // Add to mixpanel
                mixpanel.track("one-drive-integration-sign-out-clicked");

                handleSignOut();
                closeModalAll();
              }}
            >
              Sign Out
            </button>
          ) : (
            <button
              className="flex w-[225px] justify-center rounded-lg border py-2 px-4 pl-6 text-sm shadow-sm"
              onClick={() => {
                // Add to mixpanel
                mixpanel.track("one-drive-integration-clicked");

                handleSignIn();
                closeModalAll();
              }}
            >
              <img
                loading="lazy"
                className="my-1 h-4"
                src={"/imgs/logos/microsoftLogo.png"}
                alt="Microsoft Logo"
              />
              <span className="ml-[7px] w-40 pt-[1px] font-[15px]">
                Enable OneDrive
              </span>
            </button>
          )}
        </div>
        {signedInUser && nestedFolderIds.length === 0 ? (
          <input
            placeholder="Search OneDrive"
            className="border-1 m-5 mt-0 block rounded-md border-gray-300 px-4 py-3 shadow-sm sm:text-sm"
            onChange={(e) => setSearchTerm(e.target.value)}
            value={searchTerm}
            style={{ borderWidth: "1px" }}
          />
        ) : null}
        {nestedFolderIds.length > 0 ? (
          <nav className="flex px-4 pb-3 " aria-label="Breadcrumb">
            <ol role="list" className="flex items-center space-x-4">
              <li>
                <div>
                  <div
                    onClick={() => {
                      setNestedFolderIds([]);
                    }}
                    className="cursor-pointer text-gray-400 hover:text-gray-500"
                  >
                    <HomeIcon
                      className="h-5 w-5 flex-shrink-0"
                      aria-hidden="true"
                    />
                    <span className="sr-only">Home</span>
                  </div>
                </div>
              </li>
              {nestedFolderIds.map((folder: any, index: any) => (
                <li key={folder.name}>
                  <div className="flex items-center">
                    <ChevronRightIcon
                      className="h-5 w-5 flex-shrink-0 text-gray-400"
                      aria-hidden="true"
                    />
                    <div
                      onClick={() => {
                        setNestedFolderIds(nestedFolderIds.slice(0, index + 1));
                      }}
                      className="ml-4 cursor-pointer text-sm font-medium text-gray-500 hover:text-gray-700"
                    >
                      {folder.name}
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          </nav>
        ) : null}
      </div>
      {/* Create a grid of blocks with 2 columns */}
      {isFetchingOneDriveFiles ? (
        <div className="flex flex-col py-8 items-center justify-center">
          <Spinner color="black" size={48} />
        </div>
      ) : (
        <ScrollArea.Root>
          <ScrollArea.Viewport
            className="px-4 pt-3"
            style={{
              height: "100%",
            }}
          >
            <fieldset>
              <legend className="sr-only">OneDrive</legend>
              {searchTerm &&
              (documents.length === 0 || nestedFolderIds.length > 0) ? (
                <div className="flex flex-col py-8 items-center justify-center">
                  <h4 className="text-lg text-gray-700 font-medium">
                    No files found
                  </h4>
                </div>
              ) : (
                documents.map((document: any, index) => {
                  if (!document.folder) {
                    const type = getType(document);
                    return (
                      <Tooltip
                        content={`Import file`}
                        dir="left"
                        asChild={false}
                        triggerClassname="w-full h-full"
                      >
                        <OneDriveBlock
                          onClick={() => {
                            const maxUploadSize =
                              pricingPlanTierToMaxUploadSize(currentUserPlan);

                            if (document.size > maxUploadSize) {
                              addToast(
                                `Sorry, this file exceeds the maximum upload size for your current pricing plan – the maximum size is ${bytesToReadable(
                                  maxUploadSize
                                )}. Upgrade your plan to upload larger files.`,
                                {
                                  type: "error",
                                  timeout: 5000,
                                }
                              );
                              return;
                            }

                            processOneDriveAttachment(
                              document.id,
                              document.name,
                              document.file.mimeType,
                              document.size
                            );
                          }}
                          id={"one-drive-" + document.id}
                          key={index}
                          mimeType={type}
                          name={document.name}
                          modifiedTime={document.lastModifiedDateTime}
                        />
                      </Tooltip>
                    );
                  }

                  return (
                    <div
                      key={index}
                      onClick={() => {
                        setSearchTerm("");
                        setNestedFolderIds([
                          ...nestedFolderIds,
                          {
                            id: document.id,
                            name: document.name,
                          },
                        ]);
                      }}
                      className="flex cursor-pointer flex-row border-b-[1px] py-4 text-sm"
                    >
                      <div className="p-1 px-5">
                        <img
                          loading="lazy"
                          src={WindowsFolder}
                          alt={"Folder"}
                          className="h-6 w-6"
                        />
                      </div>
                      <div className="overflow-hidden text-ellipsis p-1 font-medium">
                        {document.name}
                      </div>
                      <div className="p-1 px-3 text-gray-500">
                        {moment(document.lastModifiedDateTime).format(
                          "Do MMM YYYY"
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </fieldset>
          </ScrollArea.Viewport>
          <ScrollArea.Scrollbar
            className="bg-blackA6 hover:bg-blackA8 flex touch-none select-none p-0.5 transition-colors duration-[160ms] ease-out data-[orientation=horizontal]:h-2.5 data-[orientation=vertical]:w-2.5 data-[orientation=horizontal]:flex-col"
            orientation="vertical"
          >
            <ScrollArea.Thumb className="bg-gray-900 relative flex-1 rounded-[10px] before:absolute before:top-1/2 before:left-1/2 before:h-full before:min-h-[36px] before:w-full before:min-w-[36px] before:-translate-x-1/2 before:-translate-y-1/2 before:content-['']" />
          </ScrollArea.Scrollbar>
        </ScrollArea.Root>
      )}
    </div>
  );
}

/**
 * Get the type of the document based on its properties
 * @param obj - Document object
 * @returns Type of the document
 */
const getType = (obj: any) => {
  if (obj.file) {
    return "file";
  } else if (obj.folder) {
    return "folder";
  } else if (obj.image) {
    return "image";
  } else if (obj.video) {
    return "video";
  } else if (obj.photo) {
    return "photo";
  } else if (obj.audio) {
    return "audio";
  } else {
    return "file";
  }
};
